import {
  InvoiceSchema,
  InvoiceStatsSchema,
  InvoiceStatus,
  ZodGetInvoicesPageParams,
  ZodCreateInvoiceSchema,
  ZodEditInvoiceSchema,
  ZodGetInvoicesParams,
} from "@billingnow/zod-schema";
import { PaginationMeta, PaginationParams } from "./pagination";
import { apiClient } from "./client";

export type Invoice = InvoiceSchema;
export type InvoiceImageType = "logo" | "signature";

export const getInvoice = async (invoiceId: string): Promise<Invoice> => {
  const response = await apiClient.get<{ invoice: Invoice }>(`/api/invoice/${invoiceId}`);
  return response.data.invoice;
};

/**
 * Get invoices - returns all invoices (for backward compatibility)
 * @deprecated Use getInvoicesPaginated for better performance with large datasets
 */
export const getInvoices = async (params?: ZodGetInvoicesParams): Promise<Invoice[]> => {
  const response = await apiClient.get<{ invoices: Invoice[]; pagination: PaginationMeta }>("/api/invoice", { params });
  return response.data.invoices;
};

export type InvoiceStats = InvoiceStatsSchema;

export interface InvoicesPage {
  invoices: Invoice[];
  total: number;
  /** Rows per status for the same search and filters, status excluded. */
  statusCounts: Partial<Record<InvoiceStatus, number>>;
}

/**
 * Page-based list (search, sort, page / pageSize) for the Invoices table
 */
export const getInvoicesPage = async (params: ZodGetInvoicesPageParams): Promise<InvoicesPage> => {
  const response = await apiClient.get<InvoicesPage>("/api/invoice/page", { params });
  return response.data;
};

/**
 * Outstanding, overdue, paid this month and drafts, in the organization currency
 */
export const getInvoiceStats = async (): Promise<InvoiceStats> => {
  const response = await apiClient.get<{ stats: InvoiceStats }>("/api/invoice/stats");
  return response.data.stats;
};

/**
 * Paginated response for invoices
 */
export interface GetInvoicesPaginatedResponse {
  invoices: Invoice[];
  pagination: PaginationMeta;
}

/**
 * Get invoices with pagination support
 */
export const getInvoicesPaginated = async (
  params?: ZodGetInvoicesParams & PaginationParams,
): Promise<GetInvoicesPaginatedResponse> => {
  const response = await apiClient.get<GetInvoicesPaginatedResponse>("/api/invoice", { params });
  return response.data;
};

export const createInvoice = async (
  invoiceData: ZodCreateInvoiceSchema,
): Promise<{ success: boolean; message: string; invoiceId: string }> => {
  const response = await apiClient.post<{ success: boolean; message: string; invoiceId: string }>(
    "/api/invoice",
    invoiceData,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data;
};

export const updateInvoice = async (
  invoiceData: ZodEditInvoiceSchema,
): Promise<{ success: boolean; message: string; invoice: Invoice }> => {
  const response = await apiClient.put<{ success: boolean; message: string; invoice: Invoice }>(
    `/api/invoice/${invoiceData.id}`,
    invoiceData,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data;
};

export const updateInvoiceStatus = async (
  invoiceId: string,
  status: InvoiceStatus,
): Promise<{ success: boolean; message: string; invoice: Invoice }> => {
  const response = await apiClient.patch<{ success: boolean; message: string; invoice: Invoice }>(
    `/api/invoice/${invoiceId}/status`,
    { status },
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data;
};

export const deleteInvoice = async (invoiceId: string): Promise<{ message: string; invoice: Invoice }> => {
  const response = await apiClient.delete<{ message: string; invoice: Invoice }>(`/api/invoice/${invoiceId}`);
  return response.data;
};

export const generateInvoiceNumber = async (relationId: string): Promise<{ invoiceNumber: string }> => {
  const response = await apiClient.post<{ invoiceNumber: string }>(
    "/api/invoice/generate-number",
    { relationId },
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data;
};

export interface SendInvoiceEmailData {
  to: string;
  cc?: string | null;
  bcc?: string | null;
  subject?: string | null;
  content?: string | null;
}

/**
 * Send an invoice by email. The PDF is generated server-side, and the subject /
 * content fall back to the server defaults when omitted.
 */
export const sendInvoiceEmail = async (
  invoiceId: string,
  data: SendInvoiceEmailData,
): Promise<{ success: boolean; invoiceNumber: string; sentTo: string }> => {
  const response = await apiClient.post<{ success: boolean; invoiceNumber: string; sentTo: string }>(
    `/api/invoice/${invoiceId}/send`,
    data,
    { headers: { "Content-Type": "application/json" } },
  );
  return response.data;
};

export interface SaveAndSendInvoiceData {
  id?: string;
  invoiceData: ZodCreateInvoiceSchema;
  emailData: {
    to: string;
    cc?: string | null;
    bcc?: string | null;
    subject: string;
    content: string;
    pdfContent: string;
  };
}

export const saveAndSendInvoice = async (data: SaveAndSendInvoiceData): Promise<{ message: string }> => {
  const response = await apiClient.post<{ message: string }>("/api/invoice/save-and-send", data, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data;
};

export interface SendInvoiceReminderData {
  to: string;
  cc?: string | null;
  bcc?: string | null;
  subject?: string | null;
  content?: string | null;
}

export interface SendInvoiceReminderResponse {
  success: boolean;
  invoiceNumber: string;
  sentTo: string;
  daysOverdue: number;
  reminderCount: number;
  lastReminderSentAt: string;
}

export const sendInvoiceReminder = async (
  invoiceId: string,
  data: SendInvoiceReminderData,
): Promise<SendInvoiceReminderResponse> => {
  const response = await apiClient.post<SendInvoiceReminderResponse>(`/api/invoice/${invoiceId}/remind`, data, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data;
};

/**
 * Fetch all invoices by iterating through all pages
 * Used for export functionality to get complete dataset
 */
export const getAllInvoices = async (params?: ZodGetInvoicesParams): Promise<Invoice[]> => {
  const allInvoices: Invoice[] = [];
  let cursor: string | null = null;

  do {
    const response = await getInvoicesPaginated({ ...params, cursor, limit: 100 });
    allInvoices.push(...response.invoices);
    cursor = response.pagination.nextCursor;
  } while (cursor);

  return allInvoices;
};

export default getInvoice;
